import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import MessageDisplay from './MessageDisplay';

const API_BASE = 'https://mongobyte-hc73.onrender.com/api/v1';

/**
 * Component for saving existing orders as quick reorders
 * Use this in order history, order details, or confirmation pages
 */
const SaveOrderAsQuickReorder = ({ userToken, orderId, orderData, onSaveSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderName, setOrderName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = useCallback((type, title, content, details = null) => {
    setMessage({ type, title, content, details });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  // Save existing order as quick reorder
  const saveExistingOrderAsQuickReorder = async () => {
    if (!orderName.trim()) {
      showMessage('warning', 'Name Required', 'Please enter a name for your quick reorder');
      return;
    }

    if (!orderId) {
      showMessage('error', 'Invalid Order', 'No order ID provided');
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/advanced-orders/quick-reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          orderId: orderId,
          name: orderName.trim()
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: Failed to save quick reorder`);
      }
      
      if (result.success) {
        showMessage('success', 'Quick Reorder Saved!', `"${orderName}" saved from your order!`);
        setOrderName('');
        setIsModalOpen(false);
        if (onSaveSuccess) {
          onSaveSuccess(result.data);
        }
      } else {
        throw new Error(result.message || 'Failed to save quick reorder');
      }
    } catch (error) {
      console.error('Error saving order as quick reorder:', error);
      showMessage('error', 'Save Failed', error.message || 'Failed to save quick reorder');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-generate name based on order date
  const generateOrderName = () => {
    if (orderData?.createdAt) {
      const date = new Date(orderData.createdAt);
      const formattedDate = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      setOrderName(`Reorder from ${formattedDate}`);
    } else {
      setOrderName('My Favorite Order');
    }
  };

  const canSaveQuickReorder = orderId && userToken;

  return (
    <>
      {/* Save Order as Quick Reorder Button */}
      <button
        onClick={() => {
          setIsModalOpen(true);
          generateOrderName();
        }}
        disabled={!canSaveQuickReorder}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
          canSaveQuickReorder
            ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title={!canSaveQuickReorder ? 'Order not available' : 'Save this order for quick reordering'}
      >
        <HeartIcon className="h-4 w-4" />
        <span>Save as Quick Reorder</span>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <HeartSolid className="h-6 w-6 text-red-500" />
                <h3 className="text-xl font-bold text-gray-900">Save as Quick Reorder</h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Reorder Name
                </label>
                <input
                  type="text"
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="e.g., My Weekly Lunch, Friday Combo"
                  maxLength={50}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Give this order a memorable name for easy reordering
                </p>
              </div>

              {/* Order Preview */}
              {orderData && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Order Details</h4>
                  <div className="space-y-2">
                    {orderData.meals?.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span>{item.quantity}x {item.meal?.name || 'Item'}</span>
                        <span>₦{((item.meal?.price || 0) * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-2 mt-3">
                    <div className="flex justify-between font-medium">
                      <span>Total</span>
                      <span>₦{(orderData.meals?.reduce((total, item) => total + ((item.meal?.price || 0) * item.quantity), 0) || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  {orderData.restaurant && (
                    <div className="text-sm text-gray-600 mt-2">
                      Restaurant: {orderData.restaurant.name || orderData.restaurant}
                    </div>
                  )}
                  {orderData.customId && (
                    <div className="text-xs text-gray-500 mt-1">
                      Order ID: {orderData.customId}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-900 mb-2">What happens next:</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• This order will be saved to your quick reorders</li>
                  <li>• You can instantly reorder these exact items</li>
                  <li>• Update delivery details when reordering</li>
                  <li>• Access from your profile or cart page</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveExistingOrderAsQuickReorder}
                  disabled={isSaving || !orderName.trim()}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                    isSaving || !orderName.trim()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      <span>Save Quick Reorder</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Message Display */}
      <MessageDisplay message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default SaveOrderAsQuickReorder;
